import { GlowCard } from "../../ui/glow-card.js";
import { Skeleton, SkeletonRegion } from "../../ui/skeleton.js";

/**
 * One skeleton per widget, each mirroring that widget's real layout.
 *
 * They deliberately do NOT share a single generic shape: the whole purpose is
 * that the card occupies the same footprint before and after loading, so nothing
 * on the dashboard jumps when the data lands.
 */

/** Seven stat tiles, in the same responsive grid the real row uses. */
export function KpiSummarySkeleton() {
  return (
    <SkeletonRegion label="Loading summary" className="grid grid-cols-2 items-stretch gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
      {Array.from({ length: 7 }, (_, i) => (
        <Skeleton key={i} className="h-[58px] rounded-xl" style={{ animationDelay: `${i * 70}ms` }} />
      ))}
    </SkeletonRegion>
  );
}

/** Title, four-column table header and five rows. */
export function PendingLeaveSkeleton() {
  return (
    <SkeletonRegion label="Loading pending leave requests">
      <Skeleton className="mb-4 h-4 w-44" />
      <Skeleton className="mb-2 h-8 w-full rounded-lg" />
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 border-b border-surface-200 py-2.5 last:border-0">
          <span className="min-w-0 flex-1">
            <Skeleton className="h-3 w-32" style={{ animationDelay: `${i * 70}ms` }} />
            <Skeleton className="mt-1.5 h-2.5 w-24 bg-surface-100" style={{ animationDelay: `${i * 70}ms` }} />
          </span>
          <Skeleton className="h-3 w-14 shrink-0" style={{ animationDelay: `${i * 70}ms` }} />
          <Skeleton className="h-3 w-6 shrink-0" style={{ animationDelay: `${i * 70}ms` }} />
          <Skeleton className="h-3 w-16 shrink-0" style={{ animationDelay: `${i * 70}ms` }} />
        </div>
      ))}
      <Skeleton className="mt-3 h-3 w-36 bg-surface-100" />
    </SkeletonRegion>
  );
}

/**
 * Header, period nav, three summary cards and the 196px main area — the same
 * fixed height the loaded widget uses, so switching in does not resize the card.
 */
export function AttendanceSkeleton() {
  return (
    <SkeletonRegion label="Loading attendance">
      <div className="mb-3 flex items-center justify-between gap-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-[26px] w-28 rounded-lg" />
      </div>
      <div className="mb-3 flex items-center gap-1.5">
        <Skeleton className="h-7 w-7 shrink-0 rounded-lg" />
        <Skeleton className="h-4 flex-1" />
        <Skeleton className="h-7 w-7 shrink-0 rounded-lg" />
      </div>
      <div className="mb-3 grid grid-cols-3 gap-2">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-[38px] rounded-[10px]" style={{ animationDelay: `${i * 70}ms` }} />
        ))}
      </div>
      <div className="flex h-[196px] items-center @max-[360px]:h-[146px]">
        <div className="grid w-full grid-cols-7 gap-2">
          {Array.from({ length: 7 }, (_, i) => (
            <Skeleton key={i} className="h-[108px] rounded-[10px] @max-[360px]:aspect-square @max-[360px]:h-auto" style={{ animationDelay: `${i * 60}ms` }} />
          ))}
        </div>
      </div>
      <Skeleton className="mx-auto mt-3 h-3 w-48 bg-surface-100" />
      <Skeleton className="mt-3 h-10 w-full rounded-xl" />
    </SkeletonRegion>
  );
}

/** Title plus the five action buttons. */
export function QuickActionsSkeleton() {
  const widths = ["w-32", "w-28", "w-24", "w-32", "w-28"];
  return (
    <SkeletonRegion label="Loading quick actions">
      <Skeleton className="mb-2.5 h-3.5 w-24" />
      <div className="flex flex-wrap gap-1.5">
        {widths.map((w, i) => (
          <Skeleton key={i} className={`h-8 rounded-md ${w}`} style={{ animationDelay: `${i * 70}ms` }} />
        ))}
      </div>
    </SkeletonRegion>
  );
}

/**
 * The expense report's status chips, trend panel and two chart rows. It stands in
 * both for the request and for the Recharts chunk still downloading behind it, so
 * the card holds its footprint across both waits rather than resizing twice.
 */
export function ExpenseReportSkeleton() {
  return (
    <SkeletonRegion label="Loading expense report">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {["w-16", "w-[118px]", "w-[158px]", "w-[156px]", "w-[86px]", "w-28"].map((w, i) => (
          <Skeleton key={i} className={`h-8 rounded-md ${w}`} style={{ animationDelay: `${i * 70}ms` }} />
        ))}
      </div>
      <Skeleton className="mb-5 h-[200px] w-full rounded-xl" />
      <div className="grid gap-5 @3xl:grid-cols-2">
        {Array.from({ length: 2 }, (_, i) => (
          <Skeleton key={i} className="h-[180px] rounded-xl" style={{ animationDelay: `${i * 70}ms` }} />
        ))}
      </div>
    </SkeletonRegion>
  );
}

/**
 * Placeholder cards for the grid itself, shown while the saved layout is being
 * fetched — before that resolves we do not yet know WHICH widgets to render, so
 * these stand in at the default arrangement's spans.
 */
export function DashboardGridSkeleton() {
  // Mirrors the shipped default layout (see widgets/index.ts): the summary, then the
  // two full-width reports, then attendance at a third width.
  const spans = ["md:col-span-6", "md:col-span-6", "md:col-span-6", "md:col-span-2"];
  const heights = ["h-[90px]", "h-[330px]", "h-[560px]", "h-[280px]"];
  return (
    <SkeletonRegion label="Loading dashboard" className="grid grid-cols-1 items-start gap-4 md:grid-cols-6">
      {spans.map((span, i) => (
        <div key={i} className={`col-span-1 ${span}`}>
          <GlowCard glow="ultra" spotlight="low" size="lg" className="h-full">
            <Skeleton className={`w-full ${heights[i]}`} style={{ animationDelay: `${i * 100}ms` }} />
          </GlowCard>
        </div>
      ))}
    </SkeletonRegion>
  );
}
